import { TenantId, TenantIdError } from "./tenant-id";
import { SiteId, SiteIdError } from "./site-id";

export class SiteKeyError extends Error {
  constructor(
    public readonly kind: "Format",
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
  }
}

/**
 * A composite key uniquely identifying a Site within a Tenant.
 *
 * Implements structural equality: two SiteKeys are equal if they have
 * the same tenant and site values.
 *
 * @deprecated Use TenantId and SiteId independently.
 */
export class SiteKey {
  constructor(
    readonly tenant: TenantId,
    readonly id: SiteId,
  ) {}

  /**
   * Compares this SiteKey with another for structural equality.
   *
   * @param other - The other SiteKey to compare with
   * @returns true if both tenant and site are equal, false otherwise
   */
  equals(other: SiteKey): boolean {
    return this.tenant === other.tenant && this.id === other.id;
  }

  /**
   * Returns a hash code for this SiteKey.
   *
   * Equal SiteKeys will have the same hash code, making this suitable
   * for use as a Map key or in a Set.
   *
   * @returns A string hash combining the tenant and site representations
   */
  hashCode(): string {
    return this.toString();
  }

  /**
   * Returns a string representation of this SiteKey.
   *
   * @returns A string in the format "{tenant}@{site}"
   */
  toString(): string {
    return `${TenantId.toString(this.tenant)}@${SiteId.toString(this.id)}`;
  }

  /**
   * Constructs a SiteKey from a string.
   *
   * @param str - A string in the format "{tenant}@{site}"
   * @returns A SiteKey
   */
  static fromString(str: string): SiteKey {
    const [tenantString, siteString, ...rest] = str.split("@");
    if (tenantString === undefined || siteString === undefined || rest.length > 0)
      throw new SiteKeyError(
        "Format",
        `Site key ${str} was in an incorrect format. Expected a Tenant ID and Site ID joined by '@'.`,
      );
    try {
      const tenant = TenantId.fromString(tenantString);
      const site = SiteId.fromString(siteString);
      return new SiteKey(tenant, site);
    } catch (error) {
      if (error instanceof TenantIdError) {
        throw new SiteKeyError("Format", `Site key ${str} has an invalid Tenant ID.`, { cause: error });
      }
      if (error instanceof SiteIdError) {
        throw new SiteKeyError("Format", `Site key ${str} has an invalid Site ID.`, { cause: error });
      }
      throw error;
    }
  }
}
